import "./password-change.css";
import { FormEvent, useEffect, useRef, useState } from "react";

type Props = { kind: "staff" | "guest"; onEnded: (notice: string, complete?: boolean) => void };
export default function PasswordChangeForm({ kind, onEnded }: Props) {
  const [open, setOpen] = useState(false), [busy, setBusy] = useState(false);
  const [currentPassword, setCurrent] = useState(""), [newPassword, setNew] = useState(""), [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const errorSummary = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (error) errorSummary.current?.focus(); }, [error]);
  const currentField = useRef<HTMLInputElement>(null), opener = useRef<HTMLButtonElement>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const wasOpen = useRef(false);
  useEffect(() => { if (open) currentField.current?.focus(); else if (wasOpen.current) opener.current?.focus(); wasOpen.current = open; }, [open]);
  const close = () => { setOpen(false); setCurrent(""); setNew(""); setConfirm(""); setError(""); };
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError("");
    if (newPassword !== confirm) { setError("The two new passwords are different. Type the same password twice."); return; }
    setBusy(true);
    try {
      const response = await fetch(`/api/${kind === "staff" ? "admin" : "guest"}/password`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ currentPassword, newPassword }), signal: AbortSignal.timeout(10_000)
      });
      if (!mounted.current) return;
      const body = response.status === 204 ? null : await response.json().catch(() => null);
      if (!mounted.current) return;
      if (response.status === 204) { onEnded("Your password has changed. Sign in with your new password.", true); return; }
      if (response.status === 401 || response.status === 409 || response.status >= 500 || body?.sessionEnded) {
        onEnded(body?.error || "We could not confirm the password change. Sign in again before trying again."); return;
      }
      setCurrent(""); setError(body?.error || "The password could not be changed. Check the details and try again.");
    } catch {
      if (mounted.current) onEnded("We could not confirm the password change. Reconnect and sign in again before trying again.");
    } finally { if (mounted.current) setBusy(false); }
  };
  return <section className="password-change" aria-label="Change your password">
    <button ref={opener} type="button" onClick={() => setOpen(true)} hidden={open}>Change password</button>
    {open ? <form onSubmit={submit}>
      <p>Change your own password. You will need to sign in again afterwards.</p>
      <label>Current password<input ref={currentField} type="password" autoComplete="current-password" required value={currentPassword} onChange={event => setCurrent(event.target.value)} /></label>
      <p id={`password-change-${kind}-hint`}>Use at least {kind === "guest" ? 12 : 8} characters. A few unrelated words can help.</p>
      <label>New password<input type="password" autoComplete="new-password" aria-describedby={`password-change-${kind}-hint`} required minLength={kind === "guest" ? 12 : 8} maxLength={128} value={newPassword} onChange={event => setNew(event.target.value)} /></label>
      <label>Type the new password again<input type="password" autoComplete="new-password" required value={confirm} onChange={event => setConfirm(event.target.value)} /></label>
      {error ? <p ref={errorSummary} tabIndex={-1} role="alert">{error}</p> : null}
      <button type="submit" disabled={busy}>{busy ? "Changing password…" : "Save password"}</button>
      <button type="button" disabled={busy} onClick={close}>Cancel</button>
    </form> : null}
  </section>;
}
